import React, { useEffect, useState } from 'react';
import { usersAPI, authAPI } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Users, Edit, Trash2, Plus, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, PROVIDER_TYPES } from '@/utils/constants';

const ManageClientsPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newClient, setNewClient] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
    provider_name: '',
    provider_type: '',
    role: 'client',
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      const clientUsers = response.data.filter(u => u.role === 'client');
      setUsers(clientUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await usersAPI.update(editingUser.id, {
        full_name: editingUser.full_name,
        phone: editingUser.phone,
        provider_name: editingUser.provider_name,
        provider_type: editingUser.provider_type,
      });
      toast.success('User updated successfully');
      setIsEditDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleAddClient = async (e) => {
    e.preventDefault();
    try {
      await authAPI.register(newClient);
      toast.success('Client added successfully');
      setIsAddDialogOpen(false);
      setNewClient({
        email: '',
        password: '',
        full_name: '',
        phone: '',
        provider_name: '',
        provider_type: '',
        role: 'client',
      });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add client');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await usersAPI.delete(userId);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Manage Clients & Providers
          </h1>
          <p className="text-slate-600" style={{ fontFamily: 'Public Sans, sans-serif' }}>
            View and manage all registered clients and service providers
          </p>
        </div>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-client-button" className="gap-2">
              <UserPlus className="h-4 w-4" />
              Add New Client
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Add New Client / Provider</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddClient} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label style={{ fontFamily: 'Public Sans, sans-serif' }}>Full Name *</Label>
                  <Input
                    data-testid="add-client-name"
                    value={newClient.full_name}
                    onChange={(e) => setNewClient({ ...newClient, full_name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label style={{ fontFamily: 'Public Sans, sans-serif' }}>Email *</Label>
                  <Input
                    data-testid="add-client-email"
                    type="email"
                    value={newClient.email}
                    onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label style={{ fontFamily: 'Public Sans, sans-serif' }}>Password *</Label>
                  <Input
                    data-testid="add-client-password"
                    type="password"
                    value={newClient.password}
                    onChange={(e) => setNewClient({ ...newClient, password: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label style={{ fontFamily: 'Public Sans, sans-serif' }}>Phone</Label>
                  <Input
                    data-testid="add-client-phone"
                    value={newClient.phone}
                    onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label style={{ fontFamily: 'Public Sans, sans-serif' }}>Provider Name *</Label>
                  <Input
                    data-testid="add-client-provider-name"
                    value={newClient.provider_name}
                    onChange={(e) => setNewClient({ ...newClient, provider_name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label style={{ fontFamily: 'Public Sans, sans-serif' }}>Provider Type *</Label>
                  <select
                    data-testid="add-client-provider-type"
                    className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                    value={newClient.provider_type}
                    onChange={(e) => setNewClient({ ...newClient, provider_type: e.target.value })}
                    required
                  >
                    <option value="">Select type</option>
                    {PROVIDER_TYPES.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>
              <Button type="submit" className="w-full" data-testid="submit-add-client">
                Add Client
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-200">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>All Clients</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : users.length === 0 ? (
            <div className="text-center py-8 text-slate-500">No clients found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold" style={{ fontFamily: 'Public Sans, sans-serif' }}>Name</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold" style={{ fontFamily: 'Public Sans, sans-serif' }}>Email</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold" style={{ fontFamily: 'Public Sans, sans-serif' }}>Phone</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold" style={{ fontFamily: 'Public Sans, sans-serif' }}>Provider</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold" style={{ fontFamily: 'Public Sans, sans-serif' }}>Type</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold" style={{ fontFamily: 'Public Sans, sans-serif' }}>Registered</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold" style={{ fontFamily: 'Public Sans, sans-serif' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className="py-3 px-4 text-sm font-medium text-slate-900" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                        {user.full_name}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-700" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                        {user.email}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-700" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                        {user.phone || '-'}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-700" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                        {user.provider_name || '-'}
                      </td>
                      <td className="py-3 px-4">
                        {user.provider_type ? (
                          <Badge className="bg-blue-100 text-blue-700">{user.provider_type}</Badge>
                        ) : (
                          <span className="text-sm text-slate-500">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-500" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                        {formatDate(user.created_at)}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <Dialog open={isEditDialogOpen && editingUser?.id === user.id} onOpenChange={(open) => {
                            setIsEditDialogOpen(open);
                            if (!open) setEditingUser(null);
                          }}>
                            <DialogTrigger asChild>
                              <Button
                                size="sm"
                                variant="outline"
                                data-testid={`edit-user-${user.id}`}
                                onClick={() => setEditingUser(user)}
                              >
                                <Edit className="h-3 w-3" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Edit Client</DialogTitle>
                              </DialogHeader>
                              {editingUser && (
                                <form onSubmit={handleUpdate} className="space-y-4">
                                  <div className="space-y-2">
                                    <Label style={{ fontFamily: 'Public Sans, sans-serif' }}>Full Name</Label>
                                    <Input
                                      value={editingUser.full_name}
                                      onChange={(e) => setEditingUser({ ...editingUser, full_name: e.target.value })}
                                      required
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <Label style={{ fontFamily: 'Public Sans, sans-serif' }}>Phone</Label>
                                    <Input
                                      value={editingUser.phone || ''}
                                      onChange={(e) => setEditingUser({ ...editingUser, phone: e.target.value })}
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <Label style={{ fontFamily: 'Public Sans, sans-serif' }}>Provider Name</Label>
                                    <Input
                                      value={editingUser.provider_name || ''}
                                      onChange={(e) => setEditingUser({ ...editingUser, provider_name: e.target.value })}
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <Label style={{ fontFamily: 'Public Sans, sans-serif' }}>Provider Type</Label>
                                    <Input
                                      value={editingUser.provider_type || ''}
                                      onChange={(e) => setEditingUser({ ...editingUser, provider_type: e.target.value })}
                                    />
                                  </div>
                                  <Button type="submit" className="w-full">Update</Button>
                                </form>
                              )}
                            </DialogContent>
                          </Dialog>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-user-${user.id}`}
                            onClick={() => handleDelete(user.id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ManageClientsPage;
